/**
 * The API client.
 *
 * Every request uses the session cookie. A write also sends the CSRF token
 * that the session gave the browser. The browser holds no access token and no
 * refresh token.
 *
 * The client never decides permissions. It shows the decision and the reason
 * that the server returned.
 */

import { recordRequest } from "./trail.ts";

export interface ApiProblem {
  readonly error: string;
  readonly message: string;
  readonly fields?: readonly { readonly path: string; readonly message: string }[];
  readonly currentRevision?: number;
  readonly currentValue?: unknown;
  readonly reference?: string;
}

/** Raised when the server refuses a request. It carries the readable reason. */
export class ApiError extends Error {
  public readonly status: number;
  public readonly problem: ApiProblem;

  constructor(status: number, problem: ApiProblem) {
    super(problem.message);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }

  /** True when another person saved a newer revision of the same record. */
  get isRevisionConflict(): boolean {
    return this.problem.error === "revision_conflict";
  }
}

let csrfToken = "";

/** Keep the CSRF token that the session returned. */
export function setCsrfToken(token: string): void {
  csrfToken = token;
}

/** Read the CSRF token, for a request that sends a form rather than JSON. */
export function currentCsrfToken(): string {
  return csrfToken;
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (method !== "GET" && method !== "HEAD") {
    headers["x-arcgrc-csrf"] = csrfToken;
  }
  const init: RequestInit = { method, headers, credentials: "same-origin" };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }
  const response = await fetch(path, init);
  recordRequest({
    at: new Date().toISOString(),
    method,
    path,
    status: response.status,
    correlationId: response.headers.get("x-correlation-id") ?? "",
  });
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  const payload = text === "" ? {} : (JSON.parse(text) as unknown);
  if (!response.ok) {
    throw new ApiError(response.status, payload as ApiProblem);
  }
  return payload as T;
}

/** The readable message of a refused request. */
export function problemText(error: unknown): string {
  if (error instanceof ApiError) {
    const fields = error.problem.fields ?? [];
    if (fields.length === 0) return error.problem.message;
    // The server names each field that failed, so the reader can correct it.
    const named = fields.map((field) => `${fieldLabel(field.path)}: ${field.message}`);
    return `${error.problem.message} ${named.join(" ")}`;
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

/** Turn a schema path such as `content.claims.0.effectiveDate` into words. */
function fieldLabel(path: string): string {
  const last = path.split(".").filter((part) => !/^\d+$/.test(part)).pop() ?? path;
  return last.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  remove: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "DELETE", body }),
};

// --- Shared types --------------------------------------------------------

export type Finding = "MET" | "NOT_MET" | "N/A" | "UNASSESSED";
export type WorkingState =
  | "not_started"
  | "in_progress"
  | "ready_for_review"
  | "reviewed";
export type ObjectiveConclusion =
  | "SATISFIED"
  | "NOT_SATISFIED"
  | "NOT_APPLICABLE"
  | "UNASSESSED";

export interface SessionView {
  readonly user: { readonly id: string; readonly email: string; readonly displayName: string };
  readonly csrfToken: string;
  readonly organizations: readonly {
    readonly tenantId: string;
    readonly slug: string;
    readonly name: string;
    readonly kind: string;
    readonly roles: readonly string[];
    /** The permissions the server derives from the roles. The browser shows them; it never derives them. */
    readonly permissions: readonly string[];
  }[];
  readonly selectedTenantId: string | null;
  readonly selectedWorkspaceKey: string | null;
  readonly isPlatformAdmin: boolean;
  /** The page "/" opens, decided by the server from the preference and the roles. */
  readonly home: "dashboard" | "my_work";
  /** Where a person manages their own passkeys. */
  readonly accountUrl: string | null;
  readonly features: Record<string, boolean>;
  /** The page that opens at "/": the dashboard, My work, or the default by role. */
  readonly homePreference: "default" | "dashboard" | "my_work";
}

export interface CatalogSummary {
  readonly id: string;
  readonly catalogId: string;
  readonly catalogVersion: string;
  readonly framework: string;
  readonly level: string | null;
  readonly title: string;
  readonly validationState: string;
  readonly scoringApplies: boolean;
  readonly requirementCount: number;
  readonly specificationCount: number;
  readonly objectiveCount: number;
}

export interface AssessmentRow {
  readonly id: string;
  readonly name: string;
  readonly assessmentType: string;
  readonly state: string;
  readonly periodStart: string | null;
  readonly periodEnd: string | null;
  readonly sspCurrent: boolean;
  readonly systemName: string;
  readonly catalogId: string;
  readonly catalogVersion: string;
  readonly framework: string;
  readonly level: string | null;
  readonly regulationSubpart: string | null;
  readonly requirementCount: string;
  readonly answeredCount: string;
  readonly originType: string;
  readonly sampleState: string | null;
}

export interface ControlListItem {
  readonly requirementResultId: string;
  readonly requirementPk: string;
  readonly officialId: string;
  readonly title: string;
  readonly domain: string | null;
  readonly workingState: WorkingState;
  readonly finding: Finding;
  readonly objectiveCount: number;
  readonly objectivesSatisfied: number;
  readonly sampleImplementationState: string | null;
  readonly evidenceCount: number;
  readonly reviewedEvidenceCount: number;
  /** Found met with no reviewed evidence behind it. Chased, never refused. */
  readonly evidenceMissing: boolean;
  /** Carried from the implementation statement, and not confirmed here yet. */
  readonly awaitingConfirmation: boolean;
  readonly openQuestionCount: number;
  readonly maxDeduction: number | null;
}

export interface ControlListPage {
  readonly items: readonly ControlListItem[];
  readonly nextCursor: string | null;
  readonly total: number;
}

export interface ControlDetail {
  readonly requirementResultId: string;
  readonly assessmentId: string;
  readonly requirement: {
    readonly id: string;
    readonly officialId: string;
    readonly title: string;
    readonly textVerbatim: string;
    readonly textSha256: string;
    readonly sourceLocator: string;
    readonly sourcePages: readonly number[];
    readonly catalogId: string;
    readonly catalogVersion: string;
    readonly discussion: string;
    readonly furtherDiscussion: string;
    readonly assessmentMethods: Record<string, string>;
    readonly keyReferences: readonly string[];
    readonly maxDeduction: number | null;
    readonly crossSource: Record<string, unknown>;
  };
  readonly objectives: readonly {
    readonly objectivePk: string;
    readonly officialId: string;
    readonly label: string;
    readonly textVerbatim: string;
    readonly sourceLocator: string;
    readonly conclusion: ObjectiveConclusion;
    readonly rationale: string;
    readonly revision: number;
    readonly evidenceCount: number;
    readonly sampleFinding: string | null;
  }[];
  readonly workingState: WorkingState;
  readonly finding: Finding;
  readonly partialCase: string | null;
  readonly findingReason: string | null;
  readonly approvalRef: string | null;
  readonly applicability: string;
  readonly applicabilityReason: string | null;
  readonly implementationStatement: string;
  /** Carried from the implementation statement, and not confirmed here yet. */
  readonly awaitingConfirmation: boolean;
  readonly originType: string;
  readonly sampleState: string | null;
  readonly sampleImplementationState: string | null;
  readonly sampleDetail: Record<string, unknown> | null;
  readonly ownerUserId: string | null;
  readonly assessorFinding: Finding | null;
  readonly revision: number;
  readonly evidence: readonly {
    readonly linkId: string;
    readonly evidenceVersionId: string;
    readonly evidenceId: string;
    readonly title: string;
    readonly kind: string;
    readonly version: number;
    readonly objectivePk: string | null;
    readonly reviewerConclusion: string;
  }[];
  readonly tasks: readonly {
    readonly id: string;
    readonly title: string;
    readonly state: string;
    readonly dueOn: string | null;
    readonly assigneeUserId: string | null;
  }[];
  readonly questions: readonly {
    readonly id: string;
    readonly subject: string;
    readonly state: string;
    readonly visibility: string;
    readonly dueOn: string | null;
  }[];
}

export interface ScoreView {
  readonly scoringApplies: boolean;
  readonly answered: number;
  readonly unresolved: number;
  readonly total: number;
  readonly score: {
    readonly complete: boolean;
    readonly score: number | null;
    readonly startingScore: number;
    readonly provisionalRange: { readonly worst: number; readonly best: number } | null;
    readonly deductions: readonly {
      readonly id: string;
      readonly cmmcId: string;
      readonly points: number;
      readonly maxPoints: number;
      readonly partialCase?: string;
    }[];
    readonly notApplicable: readonly { readonly id: string; readonly cmmcId: string }[];
    readonly unresolved: readonly string[];
    readonly prerequisites: readonly {
      readonly id: string;
      readonly satisfied: boolean;
      readonly detail: string;
    }[];
    readonly warnings: readonly string[];
    readonly ruleId: string;
    readonly ruleVersion: string;
    readonly qualification: string;
  } | null;
  readonly status: {
    readonly status: string;
    readonly conditions: readonly {
      readonly id: string;
      readonly satisfied: boolean;
      readonly detail: string;
      readonly citation: string;
    }[];
    readonly poamEligible: boolean;
    readonly closeoutDeadline: string | null;
    readonly warnings: readonly string[];
    readonly qualification: string;
  } | null;
}

export interface EvidenceRow {
  readonly id: string;
  readonly title: string;
  readonly kind: string;
  readonly latestVersion: number;
  readonly latestVersionId: string;
  readonly reviewState: string;
  /** The day the evidence was last reviewed. */
  readonly reviewDate: string | null;
  /** The day the yearly check is due, for accepted evidence. The server counts it. */
  readonly reviewDueOn: string | null;
  readonly classification: string;
  readonly linkCount: number;
  /** The day it was collected, which is the day freshness counts from. */
  readonly collectionDate: string | null;
  /** The controls it supports, by their official identifiers. */
  readonly controls: readonly string[];
  readonly originType: string;
  readonly sampleState: string | null;
}

/**
 * Store one image and return the version a document can point at.
 *
 * It is the evidence upload, which is the one path a file takes into
 * ArcGRC: it is scanned, it counts against the organization's quota, and it
 * becomes a version with a hash. A document's image block names that version,
 * and the export engine reads it from there.
 */
export async function uploadImage(
  file: File,
  title: string,
): Promise<{ evidenceVersionId: string; mediaType: string }> {
  const data = new FormData();
  data.set("title", title.trim() === "" ? file.name : title.trim());
  data.set("description", "An image held for a document.");
  data.set("file", file);
  const response = await fetch("/api/evidence/upload", {
    method: "POST",
    credentials: "same-origin",
    headers: { "x-arcgrc-csrf": currentCsrfToken() },
    body: data,
  });
  const payload = (await response.json()) as {
    message?: string;
    evidenceVersionId?: string;
    mediaType?: string;
  };
  if (!response.ok) {
    throw new Error(payload.message ?? "The upload did not complete.");
  }
  if (payload.evidenceVersionId === undefined) {
    throw new Error("The upload returned no version.");
  }
  return { evidenceVersionId: payload.evidenceVersionId, mediaType: payload.mediaType ?? "" };
}

/**
 * Upload one file as evidence, for a record that holds it: an agreement, a
 * customer responsibility matrix, a body of evidence. It is scanned, counts
 * against the quota, and becomes a version with a hash, like any upload.
 */
export async function uploadFile(
  file: File,
  title: string,
  description: string,
): Promise<{ evidenceVersionId: string }> {
  const data = new FormData();
  data.set("title", title.trim() === "" ? file.name : title.trim());
  data.set("description", description);
  data.set("file", file);
  const response = await fetch("/api/evidence/upload", {
    method: "POST",
    credentials: "same-origin",
    headers: { "x-arcgrc-csrf": currentCsrfToken() },
    body: data,
  });
  const payload = (await response.json()) as { message?: string; evidenceVersionId?: string };
  if (!response.ok) throw new Error(payload.message ?? "The upload did not complete.");
  if (payload.evidenceVersionId === undefined) throw new Error("The upload returned no version.");
  return { evidenceVersionId: payload.evidenceVersionId };
}
